import EventConstants from '@/core/eventConstants';
import { StorageKeys } from '@/models/storage-keys';
import eventEmitter from '@/utils/eventEmitter';

/**
 * Object that handles getting/setting/removing items in local storage.
 * If OfficeRuntime is available and we're not in Outlook, it uses OfficeRuntime.storage;
 * otherwise, it falls back to the browser's localStorage.
 */
const StorageUtils = {
  /**
   * Checks if the current environment is Outlook.
   * @returns {boolean} True if in Outlook environment, otherwise false.
   */
  isOutlook(): boolean {
    return typeof Office !== 'undefined' && !!Office.context.mailbox;
  },

  /**
   * Checks if the OfficeRuntime storage API is available.
   * In Outlook, even if OfficeRuntime is defined, the storage API isn't supported.
   * @returns {boolean} True if OfficeRuntime storage is available, otherwise false.
   */
  isOfficeRuntimeStorageAvailable(): boolean {
    return (
      typeof OfficeRuntime !== 'undefined' &&
      typeof OfficeRuntime.storage !== 'undefined' &&
      !this.isOutlook()
    );
  },

  /**
   * Retrieves a value from storage by key.
   * Uses OfficeRuntime.storage if available (not in Outlook), otherwise falls back to localStorage.
   *
   * @async
   * @method getStorageItem
   * @param {string} key - The key identifying the stored value.
   * @returns {Promise<string | undefined>} A promise that resolves to the stored value or undefined if it doesn't exist.
   */
  async getStorageItem(key: string): Promise<string | undefined> {
    if (this.isOfficeRuntimeStorageAvailable()) {
      try {
        const value = await OfficeRuntime.storage.getItem(key);
        return value === null ? undefined : value;
      } catch (error) {
        console.error(
          `Error retrieving value from OfficeRuntime for key "${key}":`,
          error
        );
        return undefined;
      }
    } else {
      // Use localStorage for Outlook or when OfficeRuntime storage isn't available
      const value = localStorage.getItem(key);
      return value === null ? undefined : value;
    }
  },

  /**
   * Stores a key-value pair in storage.
   * Uses OfficeRuntime.storage if available (not in Outlook), otherwise falls back to localStorage.
   *
   * @async
   * @method setStorageItem
   * @param {string} key - The key under which the value should be stored.
   * @param {unknown} value - The value to store.
   * @returns {Promise<void>} A promise that resolves when the item is stored.
   */
  async setStorageItem(key: string, value: unknown): Promise<void> {
    const valueToStore: string =
      typeof value !== 'string'
        ? this._convertToJsonString(value)
        : (value as string);

    if (this.isOfficeRuntimeStorageAvailable()) {
      try {
        await OfficeRuntime.storage.setItem(key, valueToStore);
      } catch (error) {
        console.error(
          `Error storing value in OfficeRuntime for key "${key}":`,
          error
        );
      }
    } else {
      // Use localStorage for Outlook or when OfficeRuntime storage isn't available
      localStorage.setItem(key, valueToStore);
    }
  },

  /**
   * Removes an item from storage by key.
   * Uses OfficeRuntime.storage if available (not in Outlook), otherwise falls back to localStorage.
   *
   * @async
   * @method removeStorageItem
   * @param {string} key - The key of the item to remove.
   * @returns {Promise<void>} A promise that resolves when the item is removed.
   */
  async removeStorageItem(key: string): Promise<void> {
    if (this.isOfficeRuntimeStorageAvailable()) {
      try {
        await OfficeRuntime.storage.removeItem(key);
      } catch (error) {
        console.error(
          `Error removing value from OfficeRuntime for key "${key}":`,
          error
        );
      }
    } else {
      // Use localStorage for Outlook or when OfficeRuntime storage isn't available
      localStorage.removeItem(key);
    }
  },

  /**
   * Clears all items from storage.
   * Uses OfficeRuntime.storage if available (not in Outlook), otherwise falls back to localStorage.
   *
   * @async
   * @method clearStorage
   * @returns {Promise<void>} A promise that resolves when the storage is cleared.
   */
  async clearStorage(): Promise<void> {
    console.debug('StorageUtils: clearStorage called on logout event');
    if (this.isOfficeRuntimeStorageAvailable()) {
      try {
        const keys = await OfficeRuntime.storage.getKeys();
        await OfficeRuntime.storage.removeItems(keys);
      } catch (error) {
        console.error('Error clearing OfficeRuntime storage:', error);
      }
    } else {
      // Use localStorage for Outlook or when OfficeRuntime storage isn't available
      localStorage.clear();
    }
  },

  /**
   * Converts an object to a JSON string.
   * If the object is a Map, it converts it to an object before stringifying.
   *
   * @param {unknown} object - The object to convert to a JSON string.
   * @returns {string} The JSON string representation of the object.
   */
  _convertToJsonString(object: unknown): string {
    if (object instanceof Map) {
      return JSON.stringify(Object.fromEntries(object));
    }
    return JSON.stringify(object);
  },

  /**
   * Clears all items from storage except for specified keys.
   *
   * @async
   * @method clearStorageExcept
   * @param {string[]} exceptKeys - The keys to preserve during the clear operation
   * @returns {Promise<void>} A promise that resolves when the storage is cleared.
   */
  async clearStorageExcept(exceptKeys?: string[]): Promise<void> {
    // If no keys are provided, clear all storage
    if (!exceptKeys || exceptKeys.length === 0) {
      console.debug(
        'StorageUtils: clearStorageExcept called with no keys, clearing all storage'
      );
      await this.clearStorage();
      return;
    }

    console.debug(
      `StorageUtils: clearStorageExcept called, preserving keys: ${exceptKeys.join(', ')}`
    );

    // Get all available keys in the storage
    const allKeys = Object.values(StorageKeys) as string[];
    // Filter out the keys to keep
    const keysToRemove = allKeys.filter(
      (key: string) => !exceptKeys.includes(key)
    );

    // Remove all keys in parallel using the removeStorageItem method
    await Promise.all(
      keysToRemove.map((key: string) => this.removeStorageItem(key))
    );
  }
};

// Clear storage on logout event except for USER_HISTORIC_CONVERSATIONS
eventEmitter.on(EventConstants.LOGOUT_EVENT, async () => {
  await StorageUtils.clearStorageExcept([
    StorageKeys.USER_HISTORIC_CONVERSATIONS
  ]);
  // Do not remove, use this as needed when developing
  // await StorageUtils.clearStorageExcept();
});

export { StorageUtils };
